import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import type { AppDispatch } from "./store";

// ---------------------- //
// TYPES
// ---------------------- //
export interface CleaningReservation {
  id: string;
  guestId: string;
  hostId: string;
  listingId: string;
  checkIn: string;
  checkOut: string;
  reservedDates: number;
  totalPaid: number;
  extras: Record<string, any>;
  extraCharges: number;
  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;
  paymentId: string;

  // base flags
  isConfirmed: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isPaid: boolean;
  hasCheckedIn: boolean;

  // derived flags
  isUpcoming: boolean;
  isOngoing: boolean;
  isHosted: boolean;
  isCompleted: boolean;
  isCheckingIn: boolean;
  isCheckingOut: boolean;
  isArrivingSoon: boolean;

  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
}

export interface CleaningGuestCredit {
  guestId: string;
  credits: number;
  lastCreditedAt?: any;
  lastUpdated?: any;
  refunded?: boolean;
  refundedAt?: any;
}

export interface CleaningRequest {
  requestId: string;
  listingId: string;
  guestId: string;
  hostId: string;
  checkIn: string;
  checkOut: string;
  isPending: boolean;
  isApproved: boolean;
  isRejected: boolean;
  createdAt?: any;
  updatedAt?: any;
  adults?: number;
  children?: number;
  infants?: number;
  pets?: number;
  selectedExtras?: string[];
  selectedTimes?: string[];
  totalHours?: number;
  reservedDuration?: number;
  rateType?: string;
}

// ---------------------- //
// HELPERS
// ---------------------- //
export const calculateCleaningStatusFlags = (checkIn: string, checkOut: string) => {
  const now = new Date();
  const today = new Date(now.toDateString());

  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);

  checkInDate.setHours(0, 0, 0, 0);
  checkOutDate.setHours(0, 0, 0, 0);

  const diffToCheckIn =
    (checkInDate.getTime() - today.getTime()) / (1000 * 3600 * 24);

  const diffFromCheckOut =
    (today.getTime() - checkOutDate.getTime()) / (1000 * 3600 * 24);

  const isCompleted = diffFromCheckOut > 7;
  const isCheckingOut = diffFromCheckOut >= 0 && diffFromCheckOut <= 7;

  return {
    isUpcoming: diffToCheckIn > 3,
    isArrivingSoon: diffToCheckIn <= 3 && diffToCheckIn > 0,
    isCheckingIn: diffToCheckIn === 0,
    isOngoing: today >= checkInDate && today < checkOutDate,
    isCheckingOut,
    isCompleted,
    isHosted: isCompleted,
  };
};

// ---------------------- //
// UPSERT RESERVATION
// ---------------------- //
export const upsertCleaningReservation = createAsyncThunk<
  CleaningReservation,
  Partial<CleaningReservation> & { paymentId: string },
  { rejectValue: string }
>("cleaningReservations/upsert", async (reservationData, { rejectWithValue }) => {
  try {
    if (!reservationData.paymentId) throw new Error("Missing paymentId");

    const reservationId = reservationData.paymentId;
    const docRef = doc(db, "cleaningReservations", reservationId);

    const flags = calculateCleaningStatusFlags(
      reservationData.checkIn!,
      reservationData.checkOut!
    );

    const existingSnap = await getDoc(docRef);

    if (existingSnap.exists()) {
      await updateDoc(docRef, {
        ...reservationData,
        ...flags,
        updatedAt: serverTimestamp(),
      });

      const existing = existingSnap.data() as CleaningReservation;
      const { id: _ignored, ...restExisting } = existing;
      const { id: _ignored2, ...restData } = reservationData;

      return { id: reservationId, ...restExisting, ...restData, ...flags } as CleaningReservation;
    } else {
      const payload: Omit<CleaningReservation, "id"> = {
        guestId:        reservationData.guestId!,
        hostId:         reservationData.hostId!,
        listingId:      reservationData.listingId!,
        checkIn:        reservationData.checkIn!,
        checkOut:       reservationData.checkOut!,
        reservedDates:  reservationData.reservedDates  || 0,
        totalPaid:      reservationData.totalPaid      || 0,
        extras:         reservationData.extras         || {},
        extraCharges:   reservationData.extraCharges   || 0,
        guestsFee:      reservationData.guestsFee      || 0,
        hostsFee:       reservationData.hostsFee       || 0,
        hostProfit:     reservationData.hostProfit     || 0,
        platformProfit: reservationData.platformProfit || 0,
        paymentId:      reservationData.paymentId,
        isConfirmed:    true,
        isCancelled:    false,
        isRefunded:     false,
        isPaid:         false,
        hasCheckedIn:   false,
        ...flags,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, payload, { merge: true });
      return { id: reservationId, ...payload } as CleaningReservation;
    }
  } catch (err: any) {
    console.error("🔥 upsertCleaningReservation error:", err);
    return rejectWithValue(err.message || "Failed to upsert cleaning reservation");
  }
});

// ---------------------- //
// CHECK DATE AVAILABILITY
// ---------------------- //
export const checkCleaningDateAvailability = createAsyncThunk<
  { available: boolean; conflict?: CleaningReservation },
  { listingId: string; checkIn: string; checkOut: string },
  { rejectValue: string }
>(
  "cleaningReservations/checkAvailability",
  async ({ listingId, checkIn, checkOut }, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "cleaningReservations"),
        where("listingId", "==", listingId)
      );
      const snapshot = await getDocs(q);

      const selectedStart = new Date(checkIn).getTime();
      const selectedEnd   = new Date(checkOut).getTime();

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as CleaningReservation;

        const existingStart = new Date(data.checkIn).getTime();
        const existingEnd   = new Date(data.checkOut).getTime();

        const isActive =
          data.isConfirmed === true &&
          data.isCancelled !== true &&
          data.isRefunded  !== true;

        if (isActive && selectedStart < existingEnd && selectedEnd > existingStart) {
          return { available: false, conflict: { ...data, id: docSnap.id } };
        }
      }

      return { available: true };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------- //
// REAL-TIME HOST LISTENER
// ---------------------- //
export const listenToCleaningHostReservations =
  (hostId: string) => (dispatch: AppDispatch) => {
    if (!hostId) return;

    const q = query(
      collection(db, "cleaningReservations"),
      where("hostId", "==", hostId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations: CleaningReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<CleaningReservation, "id">;
        const flags = calculateCleaningStatusFlags(data.checkIn, data.checkOut);
        return { ...data, id: docSnap.id, ...flags } as CleaningReservation;
      });
      dispatch(setHostReservations(reservations));
    });
  };

// ---------------------- //
// REAL-TIME GUEST LISTENER
// ---------------------- //
export const listenToCleaningGuestReservations =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const q = query(
      collection(db, "cleaningReservations"),
      where("guestId", "==", guestId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations: CleaningReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<CleaningReservation, "id">;
        const flags = calculateCleaningStatusFlags(data.checkIn, data.checkOut);
        return { ...data, id: docSnap.id, ...flags } as CleaningReservation;
      });
      dispatch(setGuestReservations(reservations));
    });
  };

// ---------------------- //
// REAL-TIME SINGLE RESERVATION LISTENER
// ---------------------- //
export const listenToCleaningReservationData =
  (reservationId: string) => (dispatch: AppDispatch) => {
    if (!reservationId) return;

    const ref = doc(db, "cleaningReservations", reservationId);

    return onSnapshot(ref, (docSnap) => {
      if (docSnap.exists()) {
        const data  = docSnap.data() as Omit<CleaningReservation, "id">;
        const flags = calculateCleaningStatusFlags(data.checkIn, data.checkOut);
        dispatch(setReservationById({ ...data, id: docSnap.id, ...flags } as CleaningReservation));
      } else {
        dispatch(setReservationById(null));
      }
    });
  };

// ---------------------- //
// RESCHEDULE
// ---------------------- //
export const rescheduleCleaningReservation = createAsyncThunk<
  CleaningReservation,
  { reservationId: string; newCheckIn: string; newCheckOut: string },
  { rejectValue: string }
>(
  "cleaningReservations/reschedule",
  async ({ reservationId, newCheckIn, newCheckOut }, { rejectWithValue }) => {
    try {
      const ref  = doc(db, "cleaningReservations", reservationId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Reservation not found");

      const existing = snap.data() as CleaningReservation;

      const reservedDates = Math.ceil(
        (new Date(newCheckOut).getTime() - new Date(newCheckIn).getTime()) /
        (1000 * 60 * 60 * 24)
      );

      const flags = calculateCleaningStatusFlags(newCheckIn, newCheckOut);

      await updateDoc(ref, {
        checkIn: newCheckIn,
        checkOut: newCheckOut,
        reservedDates,
        ...flags,
        updatedAt: serverTimestamp(),
      });

      return {
        ...existing,
        id: reservationId,
        checkIn: newCheckIn,
        checkOut: newCheckOut,
        reservedDates,
        ...flags,
      } as CleaningReservation;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------- //
// REAL-TIME LISTENER (ALL RESERVATIONS + HOST BLOCKS)
// ---------------------- //
export const listenToAllCleaningReservations =
  (checkIn?: string, checkOut?: string) =>
  (dispatch: AppDispatch) => {
    const reservationRef = collection(db, "cleaningReservations");
    const cleaningRef    = collection(db, "cleaning");

    const hasDates = Boolean(checkIn && checkOut);

    const toMidnightUTC = (dateStr: string): number => {
      const plain = dateStr.split("T")[0];
      return Date.parse(`${plain}T00:00:00Z`);
    };

    const selectedStart = hasDates ? toMidnightUTC(checkIn!) : null;
    const selectedEnd   = hasDates ? toMidnightUTC(checkOut!) : null;
    const today = toMidnightUTC(new Date().toISOString());

    const reservationBlocked = new Set<string>();
    const unavailableBlocked = new Set<string>();

    const dispatchMerged = () => {
      const merged = new Set([...reservationBlocked, ...unavailableBlocked]);
      dispatch(setBlockedListingIds(Array.from(merged)));
    };

    const unsubReservations = onSnapshot(reservationRef, (snapshot) => {
      const reservations: CleaningReservation[] = [];
      reservationBlocked.clear();

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as CleaningReservation;
        const reservation: CleaningReservation = {
          ...data,
          id:          docSnap.id,
          isConfirmed: data.isConfirmed ?? false,
          isCancelled: data.isCancelled ?? false,
          isRefunded:  data.isRefunded  ?? false,
        };
        reservations.push(reservation);

        if (!hasDates) return;

        const isActive =
          reservation.isConfirmed &&
          !reservation.isCancelled &&
          !reservation.isRefunded;

        if (!isActive) return;

        const existingStart = toMidnightUTC(reservation.checkIn);
        const existingEnd   = toMidnightUTC(reservation.checkOut);

        if (isNaN(existingStart) || isNaN(existingEnd)) return;
        if (existingEnd <= today) return;

        if (selectedStart! < existingEnd && selectedEnd! > existingStart) {
          reservationBlocked.add(reservation.listingId);
        }
      });

      dispatch(setAllReservations(reservations));
      dispatchMerged();
    });

    const unsubCleaning = onSnapshot(cleaningRef, (snapshot) => {
      if (!hasDates) return;
      unavailableBlocked.clear();

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const listingId: string = data.listingId ?? docSnap.id;
        const unavailableDates: string[] = data.unavailableDates || [];

        for (const date of unavailableDates) {
          const blockedDate = toMidnightUTC(date);
          if (isNaN(blockedDate)) continue;
          if (blockedDate < today) continue;

          if (selectedStart! <= blockedDate && blockedDate < selectedEnd!) {
            unavailableBlocked.add(listingId);
            break;
          }
        }
      });

      dispatchMerged();
    });

    return () => {
      unsubReservations();
      unsubCleaning();
    };
  };

// ---------------------- //
// REAL-TIME LISTENER BY LISTING
// ---------------------- //
export const listenToCleaningListingReservations =
  (listingId: string) => (dispatch: AppDispatch) => {
    if (!listingId) return;

    const q = query(
      collection(db, "cleaningReservations"),
      where("listingId", "==", listingId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          return {
            id:          docSnap.id,
            ...data,
            isConfirmed: data?.isConfirmed ?? false,
            isCancelled: data?.isCancelled ?? false,
            isRefunded:  data?.isRefunded  ?? false,
          } as CleaningReservation;
        })
        .filter((r) => r.isConfirmed && !r.isCancelled);

      dispatch(setListingReservations(reservations));
    });
  };

// ---------------------- //
// GUEST CREDITS LISTENER
// ---------------------- //
export const listenToCleaningGuestCredit =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const ref = doc(db, "cleaningCredits", guestId);

    return onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data    = snap.data();
        const credits = data.credits || 0;
        dispatch(setGuestCredit({ guestId, credits }));
      } else {
        dispatch(setGuestCredit({ guestId, credits: 0 }));
      }
    });
  };

// ---------------------- //
// UPSERT GUEST LIST
// ---------------------- //
export const upsertCleaningGuestList = createAsyncThunk<
  void,
  {
    reservationId: string;
    userId: string;
    listingId: string;
    guests: {
      fullName: string;
      contactNumber?: string;
      emailAddress?: string;
      passportId?: string;
      country?: string;
      dateOfBirth?: string;
      verified?: boolean;
      isPrimary?: boolean;
    }[];
  },
  { rejectValue: string }
>(
  "cleaningReservations/upsertCleaningGuestList",
  async ({ reservationId, userId, listingId, guests }, { rejectWithValue }) => {
    try {
      if (!reservationId || !userId) throw new Error("Missing reservation or user ID");

      const mainRef = doc(db, "cleaningGuestList", reservationId);

      await setDoc(
        mainRef,
        { reservationId, userId, listingId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      );

      const guestListRef = collection(mainRef, "guestList");

      for (const guest of guests) {
        const guestDocRef = doc(guestListRef);
        await setDoc(
          guestDocRef,
          { ...guest, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      const reservationRef = doc(db, "cleaningReservations", reservationId);
      await updateDoc(reservationRef, { hasGuestList: true, updatedAt: serverTimestamp() });
    } catch (err: any) {
      console.error("🔥 upsertCleaningGuestList error:", err);
      return rejectWithValue(err.message || "Failed to save guest list");
    }
  }
);

// ---------------------- //
// LISTING AVAILABILITY LISTENER
// ---------------------- //
export const listenToCleaningListingAvailability =
  (listingId: string, checkIn: string, checkOut: string) =>
  (dispatch: AppDispatch) => {
    if (!listingId || !checkIn || !checkOut) return;

    const toMidnightUTC = (dateStr: string): number =>
      Date.parse(`${dateStr.split("T")[0]}T00:00:00Z`);

    const selectedStart = toMidnightUTC(checkIn);
    const selectedEnd   = toMidnightUTC(checkOut);
    const today         = toMidnightUTC(new Date().toISOString());

    let reservationConflict: CleaningReservation | null = null;
    let unavailableConflict = false;

    const dispatchResult = () => {
      const conflict = reservationConflict;
      dispatch(setListingAvailability({
        available: !reservationConflict && !unavailableConflict,
        conflict,
      }));
    };

    // 1️⃣ Listen to reservations for this listing
    const reservationsQuery = query(
      collection(db, "cleaningReservations"),
      where("listingId", "==", listingId)
    );

    const unsubReservations = onSnapshot(reservationsQuery, (snapshot) => {
      reservationConflict = null;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as CleaningReservation;

        const isActive =
          data.isConfirmed &&
          !data.isCancelled &&
          !data.isRefunded;

        if (!isActive) continue;

        const existingStart = toMidnightUTC(data.checkIn);
        const existingEnd   = toMidnightUTC(data.checkOut);

        if (isNaN(existingStart) || isNaN(existingEnd)) continue;
        if (existingEnd <= today) continue;

        if (selectedStart < existingEnd && selectedEnd > existingStart) {
          reservationConflict = { ...data, id: docSnap.id };
          break;
        }
      }

      dispatchResult();
    });

    // 2️⃣ Listen to listing's own unavailableDates
    const listingRef = doc(db, "cleaning", listingId);

    const unsubListing = onSnapshot(listingRef, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data() as any;
      const unavailableDates: string[] = data.unavailableDates || [];

      unavailableConflict = unavailableDates.some((d: string) => {
        const blocked = toMidnightUTC(d);
        if (isNaN(blocked) || blocked < today) return false;
        return selectedStart <= blocked && blocked < selectedEnd;
      });

      dispatchResult();
    });

    return () => {
      unsubReservations();
      unsubListing();
    };
  };

// ---------------------- //
// LISTING AVAILABILITY BY HOURS
// ---------------------- //
export const listenToCleaningListingAvailabilityByHours =
  (payload: { listingId: string; date: string }) =>
  (dispatch: AppDispatch) => {
    const { listingId, date } = payload;
    if (!listingId || !date) return;

    const plainDate = date.split("T")[0];
    const listingRef = doc(db, "cleaning", listingId);

    const reservationsRef = query(
      collection(db, "cleaningReservations"),
      where("listingId", "==", listingId),
      where("isCancelled", "==", false)
    );

    let hostHours: number[] = [];
    let reservedHours: number[] = [];

    const recompute = () => {
      const merged = Array.from(new Set([...hostHours, ...reservedHours])).sort(
        (a, b) => a - b
      );

      dispatch(setHourAvailability({
        listingId,
        date: plainDate,
        disabledHours: merged,
        isFullyBlocked: merged.length === 24,
        isPartiallyBlocked: merged.length > 0 && merged.length < 24,
      }));
    };

    const unsubListing = onSnapshot(listingRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as any;
      const unavailableHours = data.unavailableHours || [];
      const entry = unavailableHours.find(
        (h: any) => h.date?.split("T")[0] === plainDate
      );
      hostHours = entry?.hours || [];
      recompute();
    });

    const unsubReservations = onSnapshot(reservationsRef, (snap) => {
      const hours: number[] = [];
      snap.forEach((docSnap) => {
        const r = docSnap.data() as any;
        if (!r.isConfirmed || r.isCancelled || r.isRefunded) return;
        const reservationDate = r.checkIn?.split("T")[0];
        if (reservationDate !== plainDate) return;
        if (r.rateType?.includes("perHour") && Array.isArray(r.reservedHours)) {
          r.reservedHours.forEach((t: string) => {
            const h = Number(t.split(":")[0]);
            if (!Number.isNaN(h)) hours.push(h);
          });
        }
      });
      reservedHours = hours;
      recompute();
    });

    return () => {
      unsubListing();
      unsubReservations();
    };
  };

// ---------------------- //
// UPSERT CLEANING REQUEST
// ---------------------- //
export const upsertCleaningRequest = createAsyncThunk<
  CleaningRequest,
  Omit<CleaningRequest, "requestId" | "isPending" | "isApproved" | "isRejected" | "createdAt" | "updatedAt">,
  { rejectValue: string }
>(
  "cleaningRequests/upsert",
  async (requestData, { rejectWithValue }) => {
    try {
      const docRef    = doc(collection(db, "CleaningRequests"));
      const requestId = docRef.id;

      const payload: Omit<CleaningRequest, "requestId"> = {
        listingId:        requestData.listingId,
        guestId:          requestData.guestId,
        hostId:           requestData.hostId,
        checkIn:          requestData.checkIn,
        checkOut:         requestData.checkOut,
        isPending:        true,
        isApproved:       false,
        isRejected:       false,
        adults:           requestData.adults          ?? 1,
        children:         requestData.children        ?? 0,
        infants:          requestData.infants         ?? 0,
        pets:             requestData.pets            ?? 0,
        selectedExtras:   requestData.selectedExtras  ?? [],
        selectedTimes:    requestData.selectedTimes   ?? [],
        totalHours:       requestData.totalHours      ?? 0,
        reservedDuration: requestData.reservedDuration ?? 0,
        rateType:         requestData.rateType        ?? "",
        createdAt:        serverTimestamp(),
        updatedAt:        serverTimestamp(),
      };

      await setDoc(docRef, payload);
      return { requestId, ...payload } as CleaningRequest;
    } catch (err: any) {
      console.error("🔥 upsertCleaningRequest error:", err);
      return rejectWithValue(err.message || "Failed to create cleaning request");
    }
  }
);

// ---------------------- //
// REAL-TIME HOST REQUEST LISTENER
// ---------------------- //
export const listenToCleaningHostRequests =
  (hostId: string) => (dispatch: AppDispatch) => {
    if (!hostId) return;

    const q = query(
      collection(db, "CleaningRequests"),
      where("hostId", "==", hostId)
    );

    return onSnapshot(q, (snapshot) => {
      const requests: CleaningRequest[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Omit<CleaningRequest, "requestId">),
        requestId: docSnap.id,
      }));
      dispatch(setHostReservationRequests(requests));
    });
  };

// ---------------------- //
// REAL-TIME LISTENER BY LISTING
// ---------------------- //
export const listenToCleaningListingRequests =
  (listingId: string) => (dispatch: AppDispatch) => {
    if (!listingId) return;

    const q = query(
      collection(db, "CleaningRequests"),
      where("listingId", "==", listingId)
    );

    return onSnapshot(q, (snapshot) => {
      const requests: CleaningRequest[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Omit<CleaningRequest, "requestId">),
        requestId: docSnap.id,
      }));
      dispatch(setListingReservationRequests(requests));
    });
  };

// ---------------------- //
// REAL-TIME GUEST REQUEST LISTENER
// ---------------------- //
export const listenToCleaningGuestRequests =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;

    const q = query(
      collection(db, "CleaningRequests"),
      where("guestId", "==", guestId)
    );

    return onSnapshot(q, (snapshot) => {
      const requests: CleaningRequest[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Omit<CleaningRequest, "requestId">),
        requestId: docSnap.id,
      }));
      dispatch(setGuestReservationRequests(requests));
    });
  };

// ---------------------- //
// SLICE
// ---------------------- //
const cleaningReservationSlice = createSlice({
  name: "cleaningReservations",
  initialState: {
    guestReservations:          [] as CleaningReservation[],
    hostReservations:           [] as CleaningReservation[],
    listingReservations:        [] as CleaningReservation[],
    reservationById:            null as CleaningReservation | null,
    allReservations:            [] as CleaningReservation[],
    blockedListingIds:          [] as string[],
    guestCredit:                null as CleaningGuestCredit | null,
    listingAvailability: {
      available: true,
      conflict:  null as CleaningReservation | null,
    },
    hourAvailability: null as {
      listingId?: string;
      date?: string;
      disabledHours: number[];
      isFullyBlocked: boolean;
      isPartiallyBlocked: boolean;
    } | null,
    hostReservationRequests:    [] as CleaningRequest[],
    guestReservationRequests:   [] as CleaningRequest[],
    listingReservationRequests: [] as CleaningRequest[],
    loading: false,
    error:   null as string | null,
  },
  reducers: {
    setGuestReservations: (state, action: PayloadAction<CleaningReservation[]>) => {
      state.guestReservations = action.payload;
    },
    setHostReservations: (state, action: PayloadAction<CleaningReservation[]>) => {
      state.hostReservations = action.payload;
    },
    setReservationById: (state, action: PayloadAction<CleaningReservation | null>) => {
      state.reservationById = action.payload;
    },
    setAllReservations: (state, action: PayloadAction<CleaningReservation[]>) => {
      state.allReservations = action.payload;
    },
    setBlockedListingIds: (state, action: PayloadAction<string[]>) => {
      state.blockedListingIds = action.payload;
    },
    setListingReservations: (state, action: PayloadAction<CleaningReservation[]>) => {
      state.listingReservations = action.payload;
    },
    setGuestCredit: (state, action: PayloadAction<CleaningGuestCredit>) => {
      state.guestCredit = action.payload;
    },
    setListingAvailability: (
      state,
      action: PayloadAction<{ available: boolean; conflict: CleaningReservation | null }>
    ) => {
      state.listingAvailability = action.payload;
    },
    setHostReservationRequests: (state, action: PayloadAction<CleaningRequest[]>) => {
      state.hostReservationRequests = action.payload;
    },
    setGuestReservationRequests: (state, action: PayloadAction<CleaningRequest[]>) => {
      state.guestReservationRequests = action.payload;
    },
    setListingReservationRequests: (state, action: PayloadAction<CleaningRequest[]>) => {
      state.listingReservationRequests = action.payload;
    },
    setHourAvailability: (
      state,
      action: PayloadAction<{
        listingId: string;
        date: string;
        disabledHours: number[];
        isFullyBlocked: boolean;
        isPartiallyBlocked: boolean;
      }>
    ) => {
      state.hourAvailability = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertCleaningReservation.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertCleaningReservation.fulfilled, (state, action) => {
        const idx = state.guestReservations.findIndex((r) => r.id === action.payload.id);
        if (idx >= 0) {
          state.guestReservations[idx] = action.payload;
        } else {
          state.guestReservations.push(action.payload);
        }
        state.loading = false;
      })
      .addCase(upsertCleaningReservation.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || "Failed to upsert reservation";
      })
      .addCase(upsertCleaningRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertCleaningRequest.fulfilled, (state, action) => {
        const idx = state.hostReservationRequests.findIndex(
          (r) => r.requestId === action.payload.requestId
        );
        if (idx >= 0) {
          state.hostReservationRequests[idx] = action.payload;
        } else {
          state.hostReservationRequests.push(action.payload);
        }
        state.loading = false;
      })
      .addCase(upsertCleaningRequest.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || "Failed to upsert request";
      });
  },
});

export const {
  setGuestReservations,
  setHostReservations,
  setReservationById,
  setAllReservations,
  setBlockedListingIds,
  setListingReservations,
  setGuestCredit,
  setListingAvailability,
  setHostReservationRequests,
  setGuestReservationRequests,
  setListingReservationRequests,
  setHourAvailability,
} = cleaningReservationSlice.actions;

export default cleaningReservationSlice.reducer;
